import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Tag } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "../../components/ui/sheet";
import { api } from "../../lib/api";
import type { AnnotationRange } from "../../types/api";

interface Props {
  docId: string;
  versionId: string;
  quote: string;
  range: AnnotationRange;
  sectionId?: string;
  onClose: () => void;
}

export function TagDrawer({ docId, versionId, quote, range, sectionId, onClose }: Props) {
  const [tag, setTag] = useState("");
  const qc = useQueryClient();

  const create = useMutation({
    mutationFn: () =>
      api.createAnnotation(docId, {
        kind: "tag",
        body: normalizeTag(tag),
        version_id: versionId,
        range,
        section_id: sectionId,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["doc-annotations", docId] });
      toast.success("Etiqueta añadida");
      onClose();
    },
    onError: (err) => toast.error(`No se pudo etiquetar: ${err}`),
  });

  const clean = normalizeTag(tag);

  return (
    <Sheet open onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="w-[380px] sm:w-[420px] p-0 flex flex-col">
        <SheetHeader>
          <SheetTitle>Etiquetar selección</SheetTitle>
          <SheetDescription>
            Guarda una etiqueta vinculada al fragmento seleccionado.
          </SheetDescription>
        </SheetHeader>
        <div className="px-4 pb-3">
          <blockquote className="border-l-2 border-(--accent-300) pl-3 text-xs italic text-(--text-secondary)">
            "{quote.length > 280 ? `${quote.slice(0, 280)}...` : quote}"
          </blockquote>
        </div>
        <div className="mt-auto border-t border-(--border-subtle) p-3">
          <div className="flex gap-2">
            <Input
              placeholder="Etiqueta"
              value={tag}
              onChange={(e) => setTag(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && clean) {
                  e.preventDefault();
                  create.mutate();
                }
              }}
              autoFocus
            />
            <Button
              variant="primary"
              size="sm"
              disabled={!clean || create.isPending}
              onClick={() => create.mutate()}
              aria-label="Guardar etiqueta"
            >
              <Tag size={13} />
            </Button>
          </div>
          <p className="mt-1 text-[10px] text-(--text-tertiary)">Enter guardar</p>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function normalizeTag(value: string): string {
  return value.trim().replace(/^#/, "").toLowerCase();
}
